/**
 * Provider du contexte d'identité (B1, issue #29).
 *
 * Alimente le contexte via GET /api/v1/users/me au montage et l'expose
 * via useCurrentUser(). Ne rend JAMAIS les children avant d'avoir fini de
 * charger : un écran de chargement ou un écran d'erreur explicite est affiché
 * à la place (« Impossible de charger votre profil »).
 *
 * @module components/auth/CurrentUserProvider
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/lib/api';
import { normalizeCurrentUser } from '@/lib/auth';
import { useAuthStore } from '@/stores/auth.store';
import type { CurrentUser, CurrentUserResponse } from '@/lib/types';
import { LoadingState } from '@/components/workflow/LoadingState';
import { ErrorState } from '@/components/workflow/ErrorState';

interface CurrentUserContextValue {
  /** Identité métier résolue (null tant que /users/me n'a pas répondu). */
  user: CurrentUser | null;
  isLoading: boolean;
  /** Message d'erreur lisible si le profil n'a pas pu être chargé. */
  error: string | null;
  reload: () => Promise<void>;
}

const CurrentUserContext = createContext<CurrentUserContextValue | null>(null);

export function CurrentUserProvider({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(isAuthenticated);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const state = useAuthStore.getState();
    if (!state.isAuthenticated) {
      setUser(null);
      setError(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiClient.get<CurrentUserResponse>('/users/me');
      const normalized = normalizeCurrentUser(response.data);
      // Rafraîchit le store (role/nom) avec la source de vérité serveur.
      state.updateUser({
        id: normalized.id,
        firstName: normalized.firstName,
        lastName: normalized.lastName,
        role: normalized.role,
      });
      setUser(normalized);
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setUser(null);
      if (status === 401) {
        // Session expirée : le refresh interceptor a déjà echoué → reconnexion.
        setError('Votre session a expire. Veuillez vous reconnecter.');
        navigate('/login', { replace: true });
      } else {
        setError('Impossible de charger votre profil.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    void load();
  }, [load, isAuthenticated]);

  const value = useMemo<CurrentUserContextValue>(
    () => ({ user, isLoading, error, reload: load }),
    [user, isLoading, error, load]
  );

  // Tant que le profil n'est pas chargé : jamais d'écran vide « qui a l'air normal ».
  if (isAuthenticated && isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingState fullScreen message="Chargement de votre profil..." />
      </div>
    );
  }

  if (isAuthenticated && error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md">
          <ErrorState
            title="Impossible de charger votre profil"
            message={error}
            onRetry={() => void load()}
          />
        </div>
      </div>
    );
  }

  return <CurrentUserContext.Provider value={value}>{children}</CurrentUserContext.Provider>;
}

/**
 * Identité métier du compte connecté (role + patientId / practitionerProfileId / centerId).
 * Doit être utilisé sous <CurrentUserProvider>.
 */
export function useCurrentUser(): CurrentUserContextValue {
  const ctx = useContext(CurrentUserContext);
  if (!ctx) {
    throw new Error('useCurrentUser must be used within a CurrentUserProvider');
  }
  return ctx;
}

export default CurrentUserProvider;
